import apiClient from './client';
import { getAttribution } from '../utils/attribution';

export const authService = {
    login: async (credentials: { email: string; password: string }) => {
        const response = await apiClient.post('/api/login', credentials);
        return response.data;
    },

    register: async (userData: { username: string; password: string; email: string; first_name?: string; last_name?: string }) => {
        const response = await apiClient.post('/api/register', { ...userData, ...getAttribution(), platform: 'web' });
        return response.data;
    },

    // Fire-and-forget: records first-touch UTM/referrer data for the just-authenticated
    // user. Called from AuthCallback.tsx after an OAuth login/signup, since the OAuth
    // redirect flow has no request body at account-creation time to carry this in.
    // Safe to call on every OAuth login — the backend only fills in currently-empty fields.
    recordSignupAttribution: async (): Promise<void> => {
        try {
            await apiClient.post('/api/v1/user/signup-attribution', getAttribution());
        } catch {
            // best-effort only
        }
    },

    getCurrentUser: async () => {
        const response = await apiClient.get('/api/v1/user');
        return response.data;
    },

    // Used only by AuthCallback after an OAuth redirect — reads the JWT
    // from the URL and stores it before fetching the user profile.
    getCurrentUserFromCallback: async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        if (token) {
            localStorage.setItem('token', token);
            const response = await apiClient.get('/api/v1/user', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return response.data;
        }
        const response = await apiClient.get('/api/v1/user');
        return response.data;
    },

    // Persists first/last name + email to the user record. Distinct from
    // userSettingService.updateSettings, which writes to UserSettings and
    // has no email field at all — routing an email change through settings
    // alone silently drops it (settings_handler.go has no email column to
    // write it into).
    updateProfile: async (data: { first_name: string; last_name: string; email: string }): Promise<void> => {
        await apiClient.put('/api/v1/user', data);
    },

    logout: async () => {
        const response = await apiClient.post('/api/v1/logout');
        return response.data;
    },

    googleLogin: async () => {
        const response = await apiClient.get('/api/auth/google');
        if (response.data.auth_url) {
            window.location.href = response.data.auth_url;
        }
    },

    appleLogin: async () => {
        const response = await apiClient.get('/api/auth/apple');
        if (response.data.auth_url) {
            window.location.href = response.data.auth_url;
        }
    },

    verifyEmail: async (token: string): Promise<void> => {
        await apiClient.get(`/api/verify-email?token=${encodeURIComponent(token)}`);
    },

    resendVerification: async (): Promise<void> => {
        await apiClient.post('/api/v1/resend-verification');
    },

    // Unauthenticated — token-based, linked from reminder emails so a logged-out
    // recipient can manage email prefs without needing to log in first.
    getEmailPreferences: async (token: string): Promise<EmailPreferences> => {
        const response = await apiClient.get<EmailPreferences>(`/api/email-preferences?token=${encodeURIComponent(token)}`);
        return response.data;
    },

    updateEmailPreferences: async (token: string, prefs: Omit<EmailPreferences, 'email'>): Promise<void> => {
        await apiClient.post('/api/email-preferences', { token, ...prefs });
    },
};

export interface EmailPreferences {
    email: string;
    email_notifications: boolean;
    daily_reminders: boolean;
    reminders_reviews_due: boolean;
}